import React, { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LOCAL_SEO } from "@/data/localSeoData";
import { CheckCircle2, ArrowRight, MapPin, Sparkles, ShieldCheck } from "lucide-react";

/**
 * LocalLandingPage — one dynamic SEO-optimised landing per (city × job) combo.
 * URL: /recrutement-<slug>  (e.g., /recrutement-serveur-lyon)
 *
 * React Router v6 does not support mid-segment `:param` bindings reliably across
 * all versions, so we parse the slug directly from `useLocation().pathname`.
 * Every one of the 15 pages sets its own <title>, meta description, OG tags and
 * JSON-LD (LocalBusiness + Service + FAQPage) so each ranks on its long-tail
 * query (e.g., "recruter serveur Lyon", "agent CNAPS Grenoble").
 */
export default function LocalLandingPage({ slugProp }) {
    const location = useLocation();
    // Prefer the explicit slug passed by App.js (bulletproof against router quirks)
    // Feb 2026: some new routes use no-prefix URLs (e.g. /personnel-salon-eurexpo-lyon)
    // — parse the actual pathname so canonical + og:url match the crawled URL.
    const slug = slugProp || location.pathname.replace(/^\/(recrutement-)?/, "").replace(/\/+$/, "");
    const canonicalPath = location.pathname.replace(/\/+$/, "") || "/";
    const canonicalUrl = `https://renforteo.fr${canonicalPath}`;
    const data = LOCAL_SEO[slug];

    useEffect(() => {
        if (!data) return;
        document.title = data.title;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("description", data.meta_desc);
        setMeta("og:title", data.title, "property");
        setMeta("og:description", data.meta_desc, "property");
        setMeta("og:type", "website", "property");
        setMeta("og:url", canonicalUrl, "property");
        setMeta("og:locale", "fr_FR", "property");
        if (data.hero_img) setMeta("og:image", data.hero_img, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", data.title);
        setMeta("twitter:description", data.meta_desc);
        if (data.hero_img) setMeta("twitter:image", data.hero_img);
        // Canonical — must reflect the actual URL (with or without /recrutement- prefix)
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", canonicalUrl);
        // JSON-LD (LocalBusiness + FAQPage combined via @graph)
        const ldId = "renforteo-local-jsonld";
        let ld = document.getElementById(ldId);
        if (!ld) {
            ld = document.createElement("script");
            ld.id = ldId;
            ld.type = "application/ld+json";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "LocalBusiness",
                    "@id": `${canonicalUrl}#business`,
                    name: `Renforteo — ${data.h1.replace(/\.$/, "")}`,
                    description: data.meta_desc,
                    url: canonicalUrl,
                    image: data.hero_img || "https://renforteo.fr/og-image.png",
                    priceRange: "€€",
                    address: {
                        "@type": "PostalAddress",
                        addressLocality: (data.city || "").charAt(0).toUpperCase() + (data.city || "").slice(1),
                        addressCountry: "FR",
                    },
                    areaServed: {
                        "@type": "City",
                        name: (data.city || "").charAt(0).toUpperCase() + (data.city || "").slice(1),
                    },
                    provider: {
                        "@type": "Organization",
                        name: "Renforteo",
                        url: "https://renforteo.fr",
                    },
                },
                {
                    "@type": "Service",
                    serviceType: data.job || "Recrutement de personnel événementiel",
                    provider: { "@type": "Organization", name: "Renforteo" },
                    areaServed: (data.city || "").charAt(0).toUpperCase() + (data.city || "").slice(1),
                    hasOfferCatalog: {
                        "@type": "OfferCatalog",
                        name: `Services Renforteo à ${(data.city || "").charAt(0).toUpperCase() + (data.city || "").slice(1)}`,
                        itemListElement: (data.benefits || []).map((b, i) => ({
                            "@type": "Offer",
                            position: i + 1,
                            name: b.title,
                            description: b.desc,
                        })),
                    },
                },
                {
                    "@type": "FAQPage",
                    mainEntity: (data.faq || []).map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
            ],
        });
        return () => {
            // cleanup canonical when navigating away (title reset handled by next page)
        };
    }, [slug, data]);

    if (!data) return <Navigate to="/" replace />;

    return (
        <div className="bg-white min-h-screen">
            <Header />
            {/* City hero image — unique per city (Lyon Fourvière, Valence Rhône vignoble, Grenoble Bastille) */}
            {data.hero_img && (
                <div
                    className="relative w-full h-[380px] md:h-[440px] bg-slate-900 overflow-hidden"
                    data-testid="local-hero-img"
                >
                    <img
                        src={data.hero_img}
                        alt={`${data.h1} — Renforteo`}
                        loading="eager"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0B192C]/40 via-transparent to-[#0B192C]/85"></div>
                    <div className="relative z-10 max-w-5xl mx-auto px-6 h-full flex flex-col justify-end pb-8">
                        <div className="inline-flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-[#F5A623] font-semibold">
                            <MapPin size={14} />
                            {data.city && data.city.toUpperCase()}
                            {data.job && <span className="text-white/70 ml-2">· {data.job.replace("-", " ")}</span>}
                        </div>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-white drop-shadow-md max-w-4xl"
                            data-testid="local-h1"
                        >
                            {data.h1}
                        </h1>
                    </div>
                    {data.hero_credit && (
                        <div className="absolute bottom-1 right-2 text-[10px] text-white/60 italic pointer-events-none">
                            {data.hero_credit}
                        </div>
                    )}
                </div>
            )}
            <div className="max-w-5xl mx-auto px-6 py-12">
                {/* Breadcrumb */}
                <nav className="text-xs text-slate-500 mb-6" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-[#F5A623]">Accueil</Link>
                    <span className="mx-2">›</span>
                    <Link to="/comment-ca-marche" className="hover:text-[#F5A623]">Recrutement</Link>
                    <span className="mx-2">›</span>
                    <span className="text-slate-700 font-medium">{data.h1}</span>
                </nav>

                {/* Intro (H1 already rendered above the fold in hero) */}
                <div className="mb-10">
                    <div className="space-y-4 text-slate-600 text-[15px] leading-relaxed max-w-3xl">
                        {(data.intro || []).map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/devis" className="btn-gold" data-testid="local-cta-devis">
                            Faire mon devis en 3 minutes <ArrowRight size={14} className="inline ml-1" />
                        </Link>
                        <Link to="/comment-ca-marche" className="btn-ghost-navy">
                            Comment ça marche ?
                        </Link>
                    </div>
                </div>

                {/* Benefits */}
                <section className="grid md:grid-cols-3 gap-5 mb-12">
                    {(data.benefits || []).map((b, i) => (
                        <div key={i} className="rounded-2xl bg-slate-50 border border-slate-200 p-6">
                            <div className="w-9 h-9 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center mb-3">
                                {i === 0 ? <ShieldCheck size={18} /> : i === 1 ? <Sparkles size={18} /> : <CheckCircle2 size={18} />}
                            </div>
                            <h2 className="font-display text-lg mb-1.5">{b.title}</h2>
                            <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                        </div>
                    ))}
                </section>

                {/* Local context */}
                {data.local_context && (
                    <section className="rounded-2xl bg-[#0B192C] text-white p-8 mb-12">
                        <div className="overline text-[#F5A623] mb-2">Contexte local</div>
                        <h2 className="font-display text-2xl mb-3">
                            Un ancrage réel sur {data.city && data.city.charAt(0).toUpperCase() + data.city.slice(1)}.
                        </h2>
                        <p className="text-sm text-white/80 leading-relaxed max-w-3xl">{data.local_context}</p>
                    </section>
                )}

                {/* FAQ */}
                <section className="mb-12">
                    <div className="overline mb-2">Questions fréquentes</div>
                    <h2 className="font-display text-2xl mb-5">Ce que nos clients nous demandent le plus.</h2>
                    <div className="space-y-3">
                        {(data.faq || []).map((f, i) => (
                            <details
                                key={i}
                                className="rounded-xl border border-slate-200 bg-white p-5 open:shadow-sm"
                                data-testid={`local-faq-${i}`}
                            >
                                <summary className="font-semibold text-sm cursor-pointer text-[#0B192C]">{f.q}</summary>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                {/* Internal links */}
                {data.linked && data.linked.length > 0 && (
                    <section className="mb-12 border-t border-slate-100 pt-8">
                        <div className="overline mb-2">Autres besoins fréquents</div>
                        <h2 className="font-display text-xl mb-4">Vous cherchez aussi…</h2>
                        <div className="flex flex-wrap gap-2">
                            {data.linked.map((s) => (
                                <Link
                                    key={s}
                                    to={`/recrutement-${s}`}
                                    className="text-xs px-4 py-2 rounded-full border border-slate-200 hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
                                    data-testid={`local-link-${s}`}
                                >
                                    {(LOCAL_SEO[s]?.h1 || s).replace(/\.$/, "")}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Bottom CTA */}
                <section className="rounded-2xl bg-gradient-to-br from-[#F5A623] to-[#e59911] text-[#0B192C] p-8 text-center">
                    <h2 className="font-display text-2xl mb-2">Prêt à recruter ?</h2>
                    <p className="text-sm mb-5 opacity-80 max-w-xl mx-auto">
                        Devis instantané, sans engagement, paiement uniquement quand vous validez.
                    </p>
                    <Link to="/devis" className="btn-navy" data-testid="local-cta-devis-bottom">
                        Faire mon devis maintenant <ArrowRight size={14} className="inline ml-1" />
                    </Link>
                </section>
            </div>
            <Footer />
        </div>
    );
}
